import * as Sentry from "@sentry/nextjs";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { NextSeo, NextSeoProps } from "next-seo";
import dynamic from "next/dynamic";
import DefaultErrorPage from "next/error";
import { useRouter } from "next/router";
import { useMemo } from "react";

import { RouteName } from "@/consts/RouteName";
import { BannerAvatarSize } from "@/consts/avatarSizes";
import { BannerSize } from "@/consts/bannerSize";
import { publicProfilePageTranslations } from "@/consts/pageTranslationNamespace";
import { PublicProfileBanner } from "@/features/banner/PublicProfileBanner";
import {
  EmptyText,
  ErrorState,
  Loading,
} from "@/features/common/components/informationContainer";
import { getIsOwner } from "@/features/common/helper/getIsOwner";
import { EmptyUser } from "@/features/user/components";
import { AvatarWithFallback } from "@/features/user/components/avatar/AvatarWithFallBack";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  ProfileChannelDocument,
  ProfileUserDocument,
  useProfileChannelQuery,
  useProfileUserQuery,
} from "@/generated/generated-hooks";
import { convertWhitespaceToPlusInSlug } from "@/helpers/common/convertWhitespaceToPlusInSlug";
import { getSlugForProfileUserPage } from "@/helpers/common/getSlugForProfileUserPage";
import { getAbsoluteChannelPath } from "@/helpers/common/path";
import { getPublicConfig } from "@/helpers/getPublicConfig";
import { noIndexChannel } from "@/helpers/seo/getRobotsIndex";
import { getServerSidePropswithApollo } from "@/server/graphql/apollo";
import { getEnabledFeatures } from "@/server/ssr/getEnabledFeatures";
import { getLoginSession } from "@/services/auth/userCookies";
import { getLogger } from "@/services/datadog/serverLogger";

const logger = getLogger("ssr.channel");

const SubscriptionPlanSection = dynamic(
  () =>
    import("@/features/subscriptionPlan/components/SubscriptionPlanSection"),
  { ssr: false },
);

const InlineChannelSelection = dynamic(
  () => import("@/features/channel/components/InlineChannelSelection"),
  { ssr: false },
);

const ProfileChannelMasonrySection = dynamic(
  () => import("@/features/user/components/ProfileChannelMasonrySection"),
  { ssr: false },
);

export default function ChannelPage() {
  const { t } = useTranslation(["profilePublic", "common"]);
  const config = getPublicConfig();
  const router = useRouter();
  const { slug } = router.query;
  const normalizedSlug = convertWhitespaceToPlusInSlug(slug as string);
  const accountNameWithoutPrefix = getSlugForProfileUserPage(
    router.query.accountName as string,
  );

  const { user: me, ready, ssrUserId } = useUserContext();

  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useProfileUserQuery({
    variables: {
      accountName: accountNameWithoutPrefix,
      avatarSize: {
        maxWidth: BannerAvatarSize.MaxWidth,
        maxHeight: BannerAvatarSize.MaxHeight,
      },
      bannerSize: {
        maxWidth: BannerSize.MaxWidth,
      },
    },
  });

  const { data, loading, error } = useProfileChannelQuery({
    variables: {
      channelSlug: normalizedSlug,
    },
  });

  const nextSeoProps: NextSeoProps = useMemo(() => {
    if (!userData || !data || loading || userLoading) return null;
    const { title, slug, description = "" } = data.channel;
    const { user } = userData;
    const noindex = noIndexChannel(user, data.channel);
    const canonical = getAbsoluteChannelPath({
      accountName: user.accountName,
      channelSlug: slug,
    });

    return {
      noindex,
      canonical,
      title: `${title} | ${config.siteName}`,
      description,
      openGraph: {
        type: "website",
        title: `${title} | ${config.siteName}`,
        images: [
          {
            url: user.avatar?.url ?? "/favicon.ico",
            width: 150,
            height: 150,
            alt: user.displayName ?? user.accountName,
          },
        ],
      },
    };
  }, [userData, data, config.siteName, loading, userLoading]);

  if (error) {
    return <ErrorState />;
  }

  if (!slug || !accountNameWithoutPrefix) {
    return <DefaultErrorPage statusCode={404} />;
  }

  if (!data || loading || !userData || userLoading) return <Loading />;
  if (!userData.user) return <EmptyUser />;
  if (!data.channel) {
    return <EmptyText message={t("profilePublic:channelEmpty")} />;
  }

  const {
    followerCount,
    _id: channelId,
    slug: channelSlug,
    description,
  } = data.channel;

  const {
    _id,
    banner,
    avatar,
    accountName,
    displayName,
    channels,
    walletAccount,
  } = userData.user;

  const isOwner = getIsOwner({
    userId: me?._id,
    writerId: _id,
    ready,
    ssrUserId,
  });

  return (
    <>
      <NextSeo {...nextSeoProps} />

      <PublicProfileBanner
        imgUrl={banner?.url ?? "/mock/uploadBanner.png"}
        avatar={
          <AvatarWithFallback
            sizeClassName="avatar-110 md:avatar-230"
            accountName={accountName}
            avatarUrl={avatar?.url}
            displayName={displayName}
            outlined={true}
          />
        }
        channel={data.channel}
        writer={userData.user}
      />

      <div className="mt-2 py-2 md:py-0 bg-gray-50">
        <div className="pt-1 pb-3 bg-white space-y-6">
          <SubscriptionPlanSection
            writerId={_id}
            channel={data.channel}
            singleChannel={channels.length <= 1}
          />
        </div>
      </div>

      <div className="mt-4 md:mt-5">
        <ProfileChannelMasonrySection
          channelDescription={description}
          followerCount={followerCount}
          walletAccount={walletAccount}
          channelId={channelId}
          slug={channelSlug}
          isOwner={isOwner}
        />
      </div>
    </>
  );
}

export const getServerSideProps = getServerSidePropswithApollo(
  async ({
    req,
    params,
    locale,
    query,
    apolloClient /* apollo client is injected here */,
  }) => {
    if ("preview" in query) {
      return {
        redirect: {
          permanent: true,
          destination: `${RouteName.Post}/${encodeURIComponent(
            query.preview as string,
          )}`,
        },
        props: {},
      };
    }

    const translationProps = {
      ...(await serverSideTranslations(locale, publicProfilePageTranslations)),
    };

    const { slug } = params;
    const accountName = getSlugForProfileUserPage(params.accountName);
    const normalizedSlug = convertWhitespaceToPlusInSlug(slug as string);

    if (!accountName || !normalizedSlug) {
      return {
        props: translationProps,
        notFound: true,
      };
    }

    await getEnabledFeatures({ apolloClient, page: "channelPage" });

    try {
      const userData = await apolloClient.query({
        query: ProfileUserDocument,
        variables: {
          accountName,
          avatarSize: {
            maxWidth: BannerAvatarSize.MaxWidth,
            maxHeight: BannerAvatarSize.MaxHeight,
          },
          bannerSize: {
            maxWidth: BannerSize.MaxWidth,
          },
        },
      });

      const channelData = await apolloClient.query({
        query: ProfileChannelDocument,
        variables: {
          channelSlug: normalizedSlug,
        },
      });

      if (!userData?.data?.user || !channelData?.data?.channel) {
        return {
          props: translationProps,
          notFound: true,
        };
      }
    } catch (error) {
      logger.error("ssr.profile.error", { error, slug });

      Sentry.captureException(error, {
        tags: { ssr: "profile", slug, normalizedSlug },
      });

      return { props: translationProps, notFound: true };
    }

    const { ssrUserId } = getLoginSession(req);

    return {
      props: { ...translationProps, ssrUserId },
    };
  },
);
